<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm :schema="schema" :state="state" :validate-on="['submit']" class="h-full" @submit="onSubmit">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            {{ equipment ? 'Edit Equipment' : 'Add Equipment' }}
                        </h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="name" label="Name">
                        <UInput v-model="state.name" size="xl" placeholder="Defibrillator" />
                    </UFormGroup>
                    <UFormGroup name="specification" label="Size / Model">
                        <UInput v-model="state.specification" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="qty" label="Quantity">
                        <UInput v-model="state.qty" type="number" inputmode="numeric" step="any" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="storage_id" label="Storage">
                        <USelectMenu v-model="state.storage_id" :options="storageOptions" value-attribute="id" option-attribute="name" size="xl" placeholder="None" />
                    </UFormGroup>
                    <UFormGroup name="condition" label="Condition">
                        <USelectMenu v-model="state.condition" :options="conditionOptions" value-attribute="value" option-attribute="label" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="situation" label="Situation">
                        <UTextarea v-model="state.situation" :rows="2" size="xl" placeholder="Start button not working properly..." />
                    </UFormGroup>
                    <UFormGroup name="action" label="Action">
                        <UTextarea v-model="state.action" :rows="2" size="xl" placeholder="Send to HDF for checkup..." />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" :loading="saving" color="black">
                            Save
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { CONDITION_META, EquipmentCondition, type FieldPost, type FieldPostEquipment } from '~/types/fieldPost'

const props = defineProps<{
    open: boolean
    post: FieldPost
    equipment?: FieldPostEquipment
}>()

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const client = useSupabaseClient()
const toast = useToast()

const conditionOptions = Object.values(EquipmentCondition).map(value => ({ value, label: CONDITION_META[value].label }))
const storageOptions = computed(() => [{ id: null, name: 'None' }, ...(props.post.storages ?? [])])

const schema = z.object({
    name: z.string().trim().min(1, 'Required'),
    qty: z.union([z.string(), z.number()]).refine(
        v => v !== '' && Number.isInteger(Number(v)) && Number(v) >= 0,
        'Enter a whole number',
    ),
    condition: z.nativeEnum(EquipmentCondition, { required_error: 'Required', invalid_type_error: 'Required' }),
    storage_id: z.string().nullable().optional(),
})

const saving = ref(false)
const state = reactive({
    name: '',
    specification: '',
    qty: '1' as string | number,
    storage_id: null as string | null,
    condition: EquipmentCondition.ok,
    situation: '',
    action: '',
})

watch(() => props.open, (open) => {
    if (!open) return
    state.name = props.equipment?.name ?? ''
    state.specification = props.equipment?.specification ?? ''
    state.qty = props.equipment?.qty ?? 1
    state.storage_id = props.equipment?.storage_id ?? null
    state.condition = props.equipment?.condition ?? EquipmentCondition.ok
    state.situation = props.equipment?.situation ?? ''
    state.action = props.equipment?.action ?? ''
})

async function onSubmit() {
    const payload = {
        post_id: props.post.id,
        name: state.name.trim(),
        specification: state.specification.trim() || null,
        qty: Number(state.qty),
        storage_id: state.storage_id,
        condition: state.condition,
        situation: state.situation.trim() || null,
        action: state.action.trim() || null,
    }

    saving.value = true
    const { error } = props.equipment
        ? await client.from('field_post_equipment').update(payload as never).eq('id', props.equipment.id)
        : await client.from('field_post_equipment').insert(payload as never)
    saving.value = false

    if (error) {
        toast.add({
            title: 'Could not save equipment.',
            description: error.message,
            color: 'red',
            icon: 'i-ph-x-circle-duotone',
        })
        return
    }

    toast.add({ title: 'Equipment saved.', color: 'green', icon: 'i-ph-check-circle-duotone' })
    emit('reload')
    dismiss()
}

function dismiss() {
    emit('update:open', false)
}
</script>
